// ──────────────────────────────────────────────
// GeoDrops — Telegram Mini App Platform Adapter
// Thin wrapper over Telegram WebApp API with
// capability gating and safe fallbacks.
// ──────────────────────────────────────────────

/** Check if we're running inside Telegram */
export function isTelegramEnvironment(): boolean {
  return Boolean(window.Telegram?.WebApp?.initData);
}

/** Get the Telegram WebApp instance (may be undefined outside Telegram) */
function getWebApp() {
  return window.Telegram?.WebApp;
}

// ── Theme ──

export interface TelegramTheme {
  bgColor: string;
  textColor: string;
  hintColor: string;
  linkColor: string;
  buttonColor: string;
  buttonTextColor: string;
  secondaryBgColor: string;
  headerBgColor: string;
  sectionBgColor: string;
  sectionHeaderTextColor: string;
  subtitleTextColor: string;
  destructiveTextColor: string;
}

export function getTheme(): TelegramTheme {
  const tp = getWebApp()?.themeParams;
  return {
    bgColor: tp?.bg_color || "#ffffff",
    textColor: tp?.text_color || "#000000",
    hintColor: tp?.hint_color || "#999999",
    linkColor: tp?.link_color || "#2481cc",
    buttonColor: tp?.button_color || "#2481cc",
    buttonTextColor: tp?.button_text_color || "#ffffff",
    secondaryBgColor: tp?.secondary_bg_color || "#f0f0f0",
    headerBgColor: tp?.header_bg_color || "#ffffff",
    sectionBgColor: tp?.section_bg_color || "#ffffff",
    sectionHeaderTextColor: tp?.section_header_text_color || "#6b7280",
    subtitleTextColor: tp?.subtitle_text_color || "#6b7280",
    destructiveTextColor: tp?.destructive_text_color || "#ef4444",
  };
}

export function applyThemeToCSS(theme: TelegramTheme): void {
  const root = document.documentElement;
  root.style.setProperty("--tg-bg", theme.bgColor);
  root.style.setProperty("--tg-text", theme.textColor);
  root.style.setProperty("--tg-hint", theme.hintColor);
  root.style.setProperty("--tg-link", theme.linkColor);
  root.style.setProperty("--tg-button", theme.buttonColor);
  root.style.setProperty("--tg-button-text", theme.buttonTextColor);
  root.style.setProperty("--tg-secondary-bg", theme.secondaryBgColor);
  root.style.setProperty("--tg-header-bg", theme.headerBgColor);
  root.style.setProperty("--tg-section-bg", theme.sectionBgColor);
  root.style.setProperty("--tg-section-header", theme.sectionHeaderTextColor);
  root.style.setProperty("--tg-subtitle", theme.subtitleTextColor);
  root.style.setProperty("--tg-destructive", theme.destructiveTextColor);
}

// ── Lifecycle ──

export function ready(): void {
  getWebApp()?.ready();
}

export function expand(): void {
  getWebApp()?.expand();
}

export function close(): void {
  getWebApp()?.close();
}

// ── Main Button ──

export function showMainButton(text: string, onClick: () => void): void {
  const btn = getWebApp()?.MainButton;
  if (!btn) return;
  btn.setText(text);
  btn.onClick(onClick);
  btn.show();
}

export function hideMainButton(): void {
  getWebApp()?.MainButton?.hide();
}

export function setMainButtonLoading(loading: boolean): void {
  const btn = getWebApp()?.MainButton;
  if (!btn) return;
  if (loading) {
    btn.showProgress();
    btn.disable();
  } else {
    btn.hideProgress();
    btn.enable();
  }
}

// ── Back Button ──

export function showBackButton(onClick: () => void): void {
  const btn = getWebApp()?.BackButton;
  if (!btn) return;
  btn.onClick(onClick);
  btn.show();
}

export function hideBackButton(): void {
  getWebApp()?.BackButton?.hide();
}

// ── Haptics ──

export function hapticImpact(style: "light" | "medium" | "heavy" = "light"): void {
  try {
    getWebApp()?.HapticFeedback?.impactOccurred(style);
  } catch {
    // Silently fail — not all clients support haptics
  }
}

export function hapticNotification(type: "error" | "success" | "warning"): void {
  try {
    getWebApp()?.HapticFeedback?.notificationOccurred(type);
  } catch {
    // Silently fail
  }
}

// ── Popup ──

export function showPopup(
  title: string,
  message: string,
  buttons: Array<{ id: string; type?: string; text: string }>
): Promise<string | null> {
  return new Promise((resolve) => {
    const wa = getWebApp();
    if (!wa?.showPopup) {
      resolve(null);
      return;
    }
    wa.showPopup({ title, message, buttons }, (buttonId: string) => {
      resolve(buttonId);
    });
  });
}

// ── Close Confirmation ──

export function enableCloseConfirmation(): void {
  getWebApp()?.enableClosingConfirmation?.();
}

export function disableCloseConfirmation(): void {
  getWebApp()?.disableClosingConfirmation?.();
}

// ── Viewport ──

export function getViewportHeight(): number {
  return getWebApp()?.viewportStableHeight || window.innerHeight;
}

export function getSafeAreaInsets() {
  const sa = getWebApp()?.safeAreaInset;
  return {
    top: sa?.top || 0,
    bottom: sa?.bottom || 0,
    left: sa?.left || 0,
    right: sa?.right || 0,
  };
}

// ── Launch Params ──

export function getStartParam(): string | undefined {
  return getWebApp()?.initDataUnsafe?.start_param;
}

// ── TypeScript declarations ──

declare global {
  interface Window {
    Telegram?: {
      WebApp?: any;
    };
  }
}
